import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Compass } from "lucide-react";
import { ThemeProvider } from "@/app/theme";
import { Button, EmptyState, ErrorState, Toaster, TooltipProvider } from "@/design";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function Root() {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <Outlet />
        <Toaster />
      </TooltipProvider>
    </ThemeProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <EmptyState
        size="lg"
        icon={<Compass />}
        title="We couldn't find that page"
        description="It may have been renamed or deleted, or the link has a typo. Nothing is wrong with your apps."
        actions={
          <Button asChild variant="primary">
            <Link to="/">Back to your projects</Link>
          </Button>
        }
      />
    </div>
  );
}

function RootError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <ErrorState
      variant="page"
      error={error}
      onRetry={() => {
        reset();
        void router.invalidate();
      }}
    />
  );
}
